import * as React from "react";
import * as ReactRedux from "react-redux";

import TextField from "material-ui/TextField";
import RaisedButton from "material-ui/RaisedButton";
import Checkbox from "material-ui/Checkbox";
import SelectField from "material-ui/SelectField";
import MenuItem from "material-ui/MenuItem";
import DatePicker from "material-ui/DatePicker";
import * as areIntlLocalesSupported from "intl-locales-supported";

import Recaptcha from "react-google-recaptcha";

import Heading from "../../components/Heading";
import WithCurrentUser from "../../components/WithCurrentUser";

import {
	postForm,
	FormValues,
	selectFormPostStatus,
	selectFormErrors,
} from "../../reducers/request_access";

// from: http://www.material-ui.com/#/components/date-picker
function getDateTimeFormat() {
	/**
	 * Use the native Intl.DateTimeFormat if available, or a polyfill if not.
	 */
	if (areIntlLocalesSupported(["fr", "fa-IR"])) {
		return global.Intl.DateTimeFormat;
	} else {
		const IntlPolyfill = require("intl");
		require("intl/locale-data/jsonp/fr");
		require("intl/locale-data/jsonp/fa-IR");
		return IntlPolyfill.DateTimeFormat;
	}
}

const RECAPTCHA_SITE_KEY: string = "6Lfqd0wUAAAAAIf2IleNVNensQ92HE4rggSnmCdL";

const DateTimeFormat = getDateTimeFormat();

const SUPPORTED_PLATFORMS = [
	{
		id: "ios",
		title: "iOS",
	},
	{
		id: "android",
		title: "Android",
	},
	{
		id: "web",
		title: "Web",
	},
];

const SUPPORTED_BUDGETS = [
	{
		id: 0,
		title: "<10K",
	},
	{
		id: 1,
		title: "10K-50K",
	},
	{
		id: 2,
		title: "50K-100K",
	},
	{
		id: 3,
		title: "100K-250K",
	},
	{
		id: 4,
		title: "250K+",
	},
];

const SUPPORTED_NUMBER_OF_USERS = [
	{
		id: 0,
		title: "1K-10K",
	},
	{
		id: 1,
		title: "10K-50K",
	},
	{
		id: 2,
		title: "50K-100K",
	},
	{
		id: 3,
		title: "100K-250K",
	},
	{
		id: 4,
		title: "250K+",
	},
];

const SUPPORTED_INDUSTRY_VERTICALS = [
	{
		id: "advertising",
		title: "Advertising & Marketing",
	},
	{
		id: "consumer",
		title: "Consumer Products & Services",
	},
	{
		id: "education",
		title: "Education",
	},
	{
		id: "energy",
		title: "Energy",
	},
	{
		id: "entertainment",
		title: "Entertainment",
	},
	{
		id: "finance",
		title: "Financial",
	},
	{
		id: "gaming",
		title: "Gaming",
	},
	{
		id: "healthcare",
		title: "Healthcare",
	},
	{
		id: "it",
		title: "Information Technology",
	},
	{
		id: "logistics",
		title: "Logistics & Transportation",
	},
	{
		id: "manufacturing",
		title: "Manufacturing",
	},
	{
		id: "media",
		title: "Media",
	},
	{
		id: "professional-services",
		title: "Professional Services",
	},
	{
		id: "retail",
		title: "Retail",
	},
	{
		id: "sports",
		title: "Sports",
	},
	{
		id: "telecom",
		title: "Telecommunications",
	},
	{
		id: "other",
		title: "Other",
	},
];

const mapStateToProps = state => ({
	postStatus: selectFormPostStatus(state),
	errors: selectFormErrors(state),
});

const mapDispatchToProps = {
	postForm,
};

interface RequestAccessPageProps {
	postStatus: string;
	postForm: (values: FormValues) => any;
	errors: Array<{ code: string; message: string; data: { field: string } }>;
}

class RequestAccessPageComponent extends React.Component<RequestAccessPageProps, any> {
	state = {
		companyName: "",
		website: "",
		projectName: "",
		projectDescription: "",
		platforms: [],
		budget: "",
		socialMediaHandles: "",
		industryVertical: "",
		email: "",
		name: "",
		teamSize: "",
		numberOfUsers: "",
		launchDate: undefined,
		captchaCode: "",
	};

	performSubmit() {
		this.props.postForm({
			user_email: this.state.email,
			user_id: undefined,
			user_name: this.state.name,
			company_name: this.state.companyName,
			website: this.state.website,
			social_media_handles: this.state.socialMediaHandles,
			project_name: this.state.projectName,
			project_description: this.state.projectDescription,
			project_platforms: this.state.platforms,
			industry_vertical: this.state.industryVertical,
			team_size: Number.parseInt(this.state.teamSize),
			budget: Number.parseInt(this.state.budget),
			number_of_users: Number.parseInt(this.state.numberOfUsers),
			launch_date: this.state.launchDate === undefined ? "" : this.state.launchDate.toISOString(),
			captcha_code: this.state.captchaCode,
		});
	}

	renderErrors() {
		const { errors } = this.props;

		return (
			<div
				style={{
					marginTop: "1em",
				}}
			>
				{errors.filter(e => e.code !== "INVALID_FIELD").map(error => (
					<div
						key={error.code}
						style={{
							color: "red",
						}}
					>
						{error.message}
					</div>
				))}
			</div>
		);
	}

	renderErrorForField(fieldId: string) {
		const error = this.props.errors.find(
			e => e.code === "INVALID_FIELD" && e.data.field === fieldId
		);

		return (
			<span
				style={{
					color: "red",
					fontSize: "0.6em",
					height: "1em",
					marginLeft: "1.61rem",
				}}
			>
				{error && error.message}
			</span>
		);
	}

	render() {
		const {
			companyName,
			website,
			projectName,
			projectDescription,
			platforms,
			budget,
			socialMediaHandles,
			email,
			name,
			teamSize,
			captchaCode,
		} = this.state;

		const { postStatus } = this.props;

		const canSubmit: boolean = captchaCode !== "";

		return (
			<form
				style={{
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Heading>Request Developer Access</Heading>

				<p
					style={{
						marginTop: "1em",
					}}
				>
					Submit this form to our team to request an API key. All fields are required.
				</p>

				<p>
					Please include links to any relevant attachments you’d like to share in the description
					box.
				</p>

				<div
					style={{
						display: "flex",
						flexDirection: "row",
						width: "70%",
						maxWidth: 745,
						flexWrap: "wrap",
						marginLeft: "1.61rem",
					}}
				>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							flex: 1,
							alignItems: "center",
							marginRight: "1rem",
						}}
					>
						<TextField
							floatingLabelText="Name *"
							value={name}
							onChange={(e, value) => this.setState({ name: value })}
							fullWidth
						/>

						{this.renderErrorForField("user_name")}

						<TextField
							floatingLabelText="Company Name *"
							value={companyName}
							onChange={(e, value) => this.setState({ companyName: value })}
							fullWidth
						/>

						{this.renderErrorForField("company_name")}

						<TextField
							floatingLabelText="Website *"
							value={website}
							onChange={(e, value) => this.setState({ website: value })}
							fullWidth
						/>

						{this.renderErrorForField("website")}

						<SelectField
							floatingLabelText="Industry Vertical *"
							value={this.state.industryVertical}
							onChange={(e, key, value) => this.setState({ industryVertical: value })}
							fullWidth
						>
							{SUPPORTED_INDUSTRY_VERTICALS.map(vertical => (
								<MenuItem value={vertical.id} key={vertical.id} primaryText={vertical.title} />
							))}
						</SelectField>

						{this.renderErrorForField("industry_vertical")}

						<SelectField
							multiple={true}
							hintText="Platform(s) *"
							value={platforms}
							onChange={(e, key, values) => this.setState({ platforms: values })}
							fullWidth
						>
							{SUPPORTED_PLATFORMS.map(platform => (
								<MenuItem
									key={platform.id}
									insetChildren={true}
									checked={platforms.includes(platform.id)}
									value={platform.id}
									primaryText={platform.title}
								/>
							))}
						</SelectField>

						{this.renderErrorForField("project_platforms")}

						<SelectField
							floatingLabelText="Expected Number of Users *"
							value={this.state.numberOfUsers}
							onChange={(e, key, value) => this.setState({ numberOfUsers: value })}
							fullWidth
						>
							{SUPPORTED_NUMBER_OF_USERS.map(numOfUsers => (
								<MenuItem
									key={numOfUsers.id}
									value={numOfUsers.id}
									primaryText={numOfUsers.title}
								/>
							))}
						</SelectField>

						{this.renderErrorForField("number_of_users")}

						<TextField
							floatingLabelText="Social Media Handles"
							value={socialMediaHandles}
							onChange={(e, value) => this.setState({ socialMediaHandles: value })}
							multiLine={true}
							rows={4}
							rowsMax={4}
							fullWidth
						/>

						{this.renderErrorForField("social_media_handles")}
					</div>

					<div
						style={{
							display: "flex",
							flexDirection: "column",
							flex: 1,
							alignItems: "center",
							marginLeft: "1rem",
						}}
					>
						<TextField
							floatingLabelText="Email *"
							value={email}
							onChange={(e, value) => this.setState({ email: value })}
							fullWidth
							type="email"
						/>

						{this.renderErrorForField("user_email")}

						<TextField
							floatingLabelText="Project Name *"
							value={projectName}
							onChange={(e, value) => this.setState({ projectName: value })}
							fullWidth
						/>

						{this.renderErrorForField("project_name")}

						<TextField
							floatingLabelText="Project Description *"
							value={projectDescription}
							onChange={(e, value) => this.setState({ projectDescription: value })}
							multiLine={true}
							rows={7}
							rowsMax={7}
							fullWidth
						/>

						{this.renderErrorForField("project_description")}

						{/* todo: expected number of users */}

						<TextField
							floatingLabelText="Development Team Size *"
							value={teamSize}
							onChange={(e, value) => this.setState({ teamSize: value })}
							fullWidth
							type="number"
							min={0}
							step={1}
						/>

						{this.renderErrorForField("team_size")}

						<SelectField
							floatingLabelText="Budget *"
							value={this.state.budget}
							onChange={(e, key, value) => this.setState({ budget: value })}
							fullWidth
						>
							{SUPPORTED_BUDGETS.map(b => (
								<MenuItem value={b.id} key={b.id} primaryText={b.title} />
							))}
						</SelectField>

						{this.renderErrorForField("budget")}

						<DatePicker
							hintText="Launch Date *"
							formatDate={
								new DateTimeFormat("en-US", {
									day: "numeric",
									month: "long",
									year: "numeric",
								}).format
							}
							value={this.state.launchDate}
							onChange={(empty, date) => this.setState({ launchDate: date })}
							style={{ width: "100%", marginTop: 10 }}
							textFieldStyle={{ width: "100%" }}
						/>

						{this.renderErrorForField("launch_date")}
					</div>
				</div>

				<Recaptcha
					sitekey={RECAPTCHA_SITE_KEY}
					onChange={newCaptchaCode => this.setState({ captchaCode: newCaptchaCode })}
					style={{
						marginTop: "3rem",
						marginLeft: "1.61rem",
					}}
				/>

				<div
					style={{
						marginTop: "3rem",
						marginLeft: "1.61rem",
					}}
				>
					<RaisedButton
						label={
							postStatus === "pending"
								? "Submitting..."
								: postStatus === "success"
									? "Thank You For Your Request"
									: "Submit"
						}
						disabled={!canSubmit || postStatus === "pending" || postStatus === "success"}
						backgroundColor="#708D9B"
						labelColor="#ffffff"
						type="submit"
						onClick={this.performSubmit.bind(this)}
					/>
				</div>

				{this.renderErrors()}
			</form>
		);
	}
}

export default ReactRedux.connect(
	mapStateToProps,
	mapDispatchToProps
)(RequestAccessPageComponent);



// WEBPACK FOOTER //
// ./src/pages/request-access/index.tsx